import { inject, Injectable } from '@angular/core';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import VectorTileGraphicOrigin from '@arcgis/core/graphic/VectorTileGraphicOrigin';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import { JepGraphic } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { NavigationLayer } from '../../layer/navigation.layer';
import {
  AMDB_FILTER,
  CIRCLE_STRATEGY,
  EID_FILTER,
  FIXEID_FILTER,
  HighlightSelectionSpecification,
  HighlightStrategy,
  ICAO_FILTER,
  ID_FILTER,
  LINE_SELECTION_STYLE,
  LINE_STRATEGY,
  POLYGON_SELECTION_STYLE,
  POLYGON_STRATEGY,
  SELECTION_COLOR,
  SELECTION_COLOR_TRANSPARENT,
  TILES_MAX_ZOOM
} from '../selected-item.constants';
import { NavigationFeatureType } from '../../typings/navigation-feature-type';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

@Injectable({
  providedIn: 'root'
})
export class FeatureHighlightService {
  private readonly _api = inject(ToolkitApiService);
  private readonly _logger = inject(NGXLogger);
  private _highlightStyleLayers: HighlightSelectionSpecification[] = [];
  private _clickOriginHighlight?: Graphic;
  private _activeHighlightGraphic?: JepGraphic;
  private _styleResetHandle?: ResourceHandle;

  addClickOriginHighlight(point: Point) {
    this.removeClickOriginHighlight();
    this._clickOriginHighlight = new Graphic({
      geometry: point,
      symbol: new SimpleMarkerSymbol({
        color: SELECTION_COLOR_TRANSPARENT,
        size: 18,
        style: 'circle',
        outline: { width: 2, color: SELECTION_COLOR }
      })
    });
    this._api.getMapView().graphics.add(this._clickOriginHighlight);
  }

  removeClickOriginHighlight() {
    if (!this._clickOriginHighlight) return;

    const graphics = this._api.getMapView().graphics;
    if (graphics.includes(this._clickOriginHighlight)) {
      const removed = graphics.remove(this._clickOriginHighlight);
      if (!removed) {
        this._logger.warn(
          'Unable to remove click origin highlight:',
          this._clickOriginHighlight
        );
      }
    } else {
      this._logger.trace(
        'No click origin highlight found in the MapView default graphics layer.'
      );
    }
    this._clickOriginHighlight = undefined;
  }

  addSelectionHighlight(graphic: JepGraphic) {
    // Ensure any previous highlight is fully removed before applying the new one.
    // Without this, out-of-order hitTest resolutions (rapid clicks) accumulate stale
    // style layers in the navigation layer, corrupting indices for future highlights.
    this.removeSelectionHighlight();

    const navLayer = this._api.getLayerById<NavigationLayer>('navigation');
    if (!navLayer) {
      this._logger.warn(
        'Navigation layer not found; cannot add selection highlight.'
      );
      return;
    }
    if (!graphic.origin) {
      this._logger.warn(
        'Graphic origin missing; cannot add selection highlight.'
      );
      return;
    }

    const highlightStyles = this._getSelectedStylesForNavType(graphic);
    if (!highlightStyles.length) {
      this._logger.trace(
        'No highlight styles generated for nav_type:',
        graphic.getAttribute('nav_type')
      );
      return;
    }

    this._highlightStyleLayers = highlightStyles;
    this._activeHighlightGraphic = graphic;
    this._applyHighlightStyleLayers(highlightStyles, graphic, navLayer);
    this._watchForStyleReset(navLayer);
  }

  removeSelectionHighlight() {
    // Remove the watcher first to prevent it from firing during layer deletion
    this._styleResetHandle?.remove();
    this._styleResetHandle = undefined;
    this._activeHighlightGraphic = undefined;

    const navLayer = this._api.getLayerById<NavigationLayer>('navigation');
    if (!navLayer) {
      this._logger.warn(
        'Navigation layer not found; cannot remove selection highlight.'
      );
    } else {
      this._highlightStyleLayers.forEach((layer) =>
        navLayer.deleteStyleLayer(layer.id)
      );
    }
    this._highlightStyleLayers = [];
  }

  private _applyHighlightStyleLayers(
    highlightStyles: HighlightSelectionSpecification[],
    graphic: JepGraphic,
    navLayer: NavigationLayer
  ): void {
    if (!graphic.origin) return;

    // Deep-copy the style so the splice doesn't mutate the layer's internal state
    const currentStyleInfo = navLayer.currentStyleInfo;
    const currentStyles = {
      ...currentStyleInfo,
      style: {
        ...currentStyleInfo.style,
        layers: [...currentStyleInfo.style.layers]
      }
    };

    const vtOrigin = graphic.origin as VectorTileGraphicOrigin;
    currentStyles.style.layers.splice(
      vtOrigin.layerIndex,
      0,
      ...highlightStyles
    );
    navLayer.style = currentStyles;

    highlightStyles.forEach((style, index) => {
      navLayer.setStyleLayer(style, vtOrigin.layerIndex + index);
      navLayer.setStyleLayerVisibility(style.id, 'visible');
    });
  }

  private _watchForStyleReset(navLayer: NavigationLayer): void {
    this._styleResetHandle?.remove();
    this._styleResetHandle = reactiveUtils.watch(
      () => navLayer.currentStyleInfo?.style?.layers?.length,
      () => {
        if (!this._activeHighlightGraphic || !this._highlightStyleLayers.length) {
          return;
        }
        const highlightId = this._highlightStyleLayers[0]?.id;
        const hasHighlight = navLayer.currentStyleInfo?.style?.layers?.some(
          (l: HighlightSelectionSpecification) => l.id === highlightId
        );
        if (!hasHighlight) {
          this._logger.trace(
            '[FeatureHighlightService] Style reset detected; reapplying airway highlight.'
          );
          this._applyHighlightStyleLayers(
            this._highlightStyleLayers,
            this._activeHighlightGraphic,
            navLayer
          );
        }
      }
    );
  }

  private _getSelectedStylesForNavType(
    graphic: JepGraphic
  ): HighlightSelectionSpecification[] {
    const type = graphic.getAttribute('nav_type') as NavigationFeatureType;
    const styleLayerFnMap = new Map<
      NavigationFeatureType,
      () => HighlightSelectionSpecification[]
    >([
      ['amdb', () => this.createAmdbSelectionStyleLayers(graphic) || []],
      [
        'airport',
        () =>
          this._createSimpleStyleLayer(
            graphic,
            CIRCLE_STRATEGY,
            ICAO_FILTER(graphic)
          )
      ],
      [
        'airspace',
        () =>
          this._createSimpleStyleLayer(
            graphic,
            POLYGON_STRATEGY,
            EID_FILTER(graphic)
          )
      ],
      [
        'airway',
        () =>
          this._createSimpleStyleLayer(
            graphic,
            LINE_STRATEGY,
            EID_FILTER(graphic)
          )
      ],
      [
        'navaid',
        () =>
          this._createSimpleStyleLayer(
            graphic,
            CIRCLE_STRATEGY,
            FIXEID_FILTER(graphic)
          )
      ],
      ['runway', () => this.createAmdbSelectionStyleLayers(graphic) || []],
      [
        'waypoint',
        () =>
          this._createSimpleStyleLayer(
            graphic,
            CIRCLE_STRATEGY,
            ID_FILTER(graphic)
          )
      ]
    ]);
    const styleFactory = styleLayerFnMap.get(type);
    return styleFactory ? styleFactory() : [];
  }

  private createAmdbSelectionStyleLayers(
    graphic: Graphic
  ): HighlightSelectionSpecification[] | void {
    if (!graphic.origin) {
      this._logger.warn('Graphic origin is undefined');
      return []; // or handle appropriately
    }
    const navLayer = this._api.getLayerById<NavigationLayer>('navigation');
    if (!navLayer) {
      this._logger.warn(
        'Navigation layer not found; cannot create AMDB selection layers.'
      );
      return;
    }
    if (!graphic.origin) {
      this._logger.warn(
        'Graphic origin missing; cannot create AMDB selection layers.'
      );
      return;
    }

    const vtOrigin = graphic.origin as VectorTileGraphicOrigin;
    const sourceStyleLayer = navLayer.getStyleLayer(vtOrigin.layerId);
    if (!sourceStyleLayer) {
      this._logger.warn(
        'Source style layer not found:',
        vtOrigin.layerId
      );
      return;
    }

    return [
      this._createStyleLayer(
        sourceStyleLayer,
        graphic,
        'line',
        AMDB_FILTER(graphic),
        LINE_SELECTION_STYLE,
        2,
        12
      ),
      this._createStyleLayer(
        sourceStyleLayer,
        graphic,
        'fill',
        AMDB_FILTER(graphic),
        POLYGON_SELECTION_STYLE,
        12,
        17
      )
    ];
  }

  private _createStyleLayer(
    sourceStyleLayer: HighlightSelectionSpecification,
    graphic: Graphic,
    type: 'circle' | 'line' | 'fill',
    filter: unknown,
    paint: unknown,
    minzoom: number,
    maxzoom: number
  ): HighlightSelectionSpecification {
    return {
      ...sourceStyleLayer,
      type,
      id: `${(graphic.origin as VectorTileGraphicOrigin)?.layerId}/Selected/${type.charAt(0).toUpperCase() + type.slice(1)}`,
      filter: filter as any,
      paint: paint as any,
      minzoom,
      maxzoom
    };
  }

  private _createSimpleStyleLayer(
    graphic: JepGraphic,
    selectionStrategy: HighlightStrategy,
    filter: unknown
  ): HighlightSelectionSpecification[] {
    if (!graphic.origin) {
      this._logger.warn('Graphic origin is undefined');
      return [];
    }

    const navLayer = this._api.getLayerById<NavigationLayer>('navigation');
    if (!navLayer) return [];

    const clickedLayerId = (graphic.origin as VectorTileGraphicOrigin).layerId;
    const navType = graphic.getAttribute('nav_type') as NavigationFeatureType;

    // The tile service uses multiple style layers per nav type — one per zoom range —
    // each with its own source-layer, minzoom, and maxzoom. Cloning only the clicked
    // layer leaves other zoom ranges without a highlight. Find all sibling layers
    // (same theme/IFR/type prefix) and create a highlight for each.
    let siblingLayers = this._findSiblingStyleLayers(navLayer, navType, clickedLayerId, selectionStrategy.type);

    // For circle strategy: if no circle-type siblings were found, the feature is
    // likely rendered as symbol layers (airports, navaids, and waypoints use icon
    // symbols, not circles). Retry using the clicked layer's own type so all zoom
    // ranges are covered — the cloned layers have their type and paint overridden to
    // circle below, which MapLibre handles correctly.
    if (!siblingLayers.length && selectionStrategy.type === 'circle') {
      const clickedLayer = navLayer.getStyleLayer(clickedLayerId);
      if (clickedLayer?.type) {
        siblingLayers = this._findSiblingStyleLayers(navLayer, navType, clickedLayerId, clickedLayer.type);
      }
    }

    this._logger.trace(
      `[FeatureHighlightService] siblings found (${siblingLayers.length}):`,
      siblingLayers.map((l) => l.id)
    );

    if (!siblingLayers.length) {
      this._logger.warn(
        '[FeatureHighlightService] No sibling style layers found for:',
        clickedLayerId
      );
      return [];
    }

    return siblingLayers.map((sourceLayer, index) => ({
      ...sourceLayer,
      id: `${clickedLayerId}/Selected/${index}`,
      type: selectionStrategy.type,
      paint: selectionStrategy.paint as any,
      filter: filter as any,
    }));
  }

  /**
   * Returns all style layers in the current nav layer style that share the same
   * theme/IFR/nav-type path prefix as the clicked layer AND match the given
   * `layerType`.
   *
   * Filtering by type is critical: the prefix alone matches every layer under
   * e.g. `Light/Low/Airway/` — including symbol masks, labels, and other
   * non-feature layers — which would flood MapLibre with hundreds of invalid
   * highlight specs and corrupt its feature index. Only layers whose type
   * matches the requested value reference PBF source-layers that carry the
   * feature attributes used by the highlight filter.
   *
   * `layerType` accepts any string so callers can retry with the clicked
   * layer's own type (e.g. `'symbol'`) when no strategy-type siblings exist.
   */
  private _findSiblingStyleLayers(
    navLayer: NavigationLayer,
    navType: NavigationFeatureType,
    clickedLayerId: string,
    layerType: string
  ): HighlightSelectionSpecification[] {
    const navTypeCapitalized =
      navType.charAt(0).toUpperCase() + navType.slice(1);
    const marker = `/${navTypeCapitalized}/`;
    const markerIndex = clickedLayerId.indexOf(marker);

    if (markerIndex === -1) {
      // Cannot determine the prefix — fall back to the single clicked layer
      // only if its type matches the strategy
      const fallback = navLayer.getStyleLayer(clickedLayerId);
      return fallback?.type === layerType ? [fallback] : [];
    }

    // e.g. 'Light/Low/Airway/' — everything up to and including the type segment
    const prefix = clickedLayerId.substring(0, markerIndex + marker.length);
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const siblingRegex = new RegExp(`^${escapedPrefix}`);

    const allLayers: HighlightSelectionSpecification[] =
      navLayer.currentStyleInfo?.style?.layers ?? [];

    return allLayers.filter(
      (l) => siblingRegex.test(l.id) && l.type === layerType
    );
  }
}
